// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useScenariosStore } from '@/stores/scenarios';
import { useSnackbarStore } from '@/stores/snackbar';
import to from 'await-to-js';
import axios from 'axios';
import { each, find, isNull, keyBy, without, mapValues } from 'lodash';
import { defineStore } from 'pinia';
import { ref, shallowRef, computed } from 'vue';
import i18n from '@/plugins/vue-i18n';
import handleErrorMessages from './utils/validation';

export const useScenarioCdtsStore = defineStore('scenarioCdts', () => {
  const scenariosStore = useScenariosStore();
  const snackbarStore = useSnackbarStore();

  const cdtSelectedAttributes = ref({});
  const scenarioCdts = ref([]);
  const loading = shallowRef(false);

  const getCdtSelectedAttributesForCannGroup = computed(() => cannGroupId => {
    return cdtSelectedAttributes.value[cannGroupId];
  });

  const childrenLookup = computed(() => cannGroupId => {
    // This returns us a list of all the children of each CDT
    // This is used to structure and aggregate the canvas page

    const { cdt } = find(scenarioCdts.value, { cannGroupId });

    // we have to traverse the tree up to the parents, this cuts down on finds
    const idLookup = keyBy(cdt, '_id');

    const childTree = {};
    each(cdt, node => {
      // include the current ID - this is useful on the canvas page for aggregations.
      // leads to output where, given A has a child B with a child C, we get { A: Set([A, B, C])}
      childTree[node._id] = new Set([node._id]);

      let currentParent = node.parentId;
      const currentId = node._id;

      while (!isNull(currentParent)) {
        childTree[currentParent].add(currentId);
        currentParent = idLookup[currentParent].parentId;
      }
    });

    return childTree;
  });

  const cdtById = computed(() =>
    mapValues(keyBy(scenarioCdts.value, 'cannGroupId'), cannGroup => keyBy(cannGroup.cdt, '_id'))
  );

  // Actions
  async function fetchScenarioCdts({ params } = {}) {
    const scenarioId = scenariosStore.selectedScenario._id;
    loading.value = true;
    const [err, response] = await to(axios.get(`/api/scenarios/${scenarioId}/cdts`, { params }));
    loading.value = false;
    if (err) throw new Error(err.message);
    scenarioCdts.value = response.data;
  }
  async function afterCdtJobComplete() {
    fetchScenarioCdts();
    const content = i18n.t('notifications.cdt.finished');
    snackbarStore.showSuccess(content);
  }

  async function afterCdtJobFailed() {
    fetchScenarioCdts();
    const content = i18n.t('notifications.cdt.failed');
    snackbarStore.showError(content);
  }
  async function updateCdtById(payload) {
    const scenarioId = scenariosStore.selectedScenario._id;
    const { cdtId, updates, cannGroupId } = payload;
    loading.value = true;
    const [err] = await to(
      axios.patch(`/api/scenarios/${scenarioId}/cdts/${cdtId}`, {
        cannGroupId,
        updates,
      })
    );
    loading.value = false;
    if (err) throw new Error(err.message);
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
  }
  async function addCdt(payload) {
    const scenarioId = scenariosStore.selectedScenario._id;
    const { cannGroupId } = payload;
    loading.value = true;
    const [err] = await to(
      axios.post(`/api/scenarios/${scenarioId}/cdts/cann-groups/${cannGroupId}`, payload)
    );
    if (err) throw new Error(err.message);
    fetchScenarioCdts(scenarioId);
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
    loading.value = false;
  }

  async function runCdtGenerationJob(payload) {
    const scenarioId = scenariosStore.selectedScenario._id;
    const { cannGroupIds } = payload;
    loading.value = true;
    const [err, response] = await to(
      axios.post(`/api/scenarios/${scenarioId}/cdt-generation`, { cannGroupIds })
    );
    loading.value = false;
    if (err) throw new Error(err);
    handleErrorMessages({ response });
  }

  async function deleteModifiedCdt(payload) {
    const scenarioId = scenariosStore.selectedScenario._id;
    const { cdtId } = payload;
    loading.value = true;
    const [err] = await to(axios.delete(`/api/scenarios/${scenarioId}/cdts/${cdtId}`));
    loading.value = false;
    if (err) throw new Error(err.message);
    fetchScenarioCdts(scenarioId);
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
  }
  function deleteUnsavedModifiedCdt(modifiedCdt) {
    loading.value = true;
    const newScenarioCdts = without(scenarioCdts.value, modifiedCdt);
    scenarioCdts.value = newScenarioCdts;
    loading.value = false;
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
  }
  async function copyAttributeSelection({ fromCannGroupId, toCannGroups }) {
    const scenarioId = scenariosStore.selectedScenario._id;
    loading.value = true;
    const [err] = await to(
      axios.patch(`/api/scenarios/${scenarioId}/cdt/${fromCannGroupId}/attributes/copy`, {
        toCannGroups,
      })
    );
    if (err) {
      loading.value = false;
      throw new Error(err.message);
    }
    fetchScenarioCdts(scenarioId);
    loading.value = false;
    snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
  }

  function setCdtSelectedAttributes(attributes) {
    cdtSelectedAttributes.value[attributes.key] = attributes.value;
  }

  function setScenarioCdts(newScenarioCdts) {
    scenarioCdts.value = newScenarioCdts;
  }

  function addUserModifiedCdt(scenarioCdt) {
    scenarioCdts.value.push(scenarioCdt);
  }
  return {
    cdtSelectedAttributes,
    scenarioCdts,
    loading,
    getCdtSelectedAttributesForCannGroup,
    childrenLookup,
    cdtById,
    fetchScenarioCdts,
    afterCdtJobComplete,
    afterCdtJobFailed,
    updateCdtById,
    addCdt,
    runCdtGenerationJob,
    deleteModifiedCdt,
    deleteUnsavedModifiedCdt,
    copyAttributeSelection,
    setCdtSelectedAttributes,
    setScenarioCdts,
    addUserModifiedCdt,
  };
});
